import React from "react";

import { Box, Theme } from "@mui/material";
import differenceInDays from "date-fns/differenceInDays";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";

import Days from "./components/Days";
import EndDate from "./components/EndDate";
import Name from "./components/Name";
import OptionMenu from "./components/OptionMenu";
import PublishPlanSwitch from "./components/PublishPlanSwitch";
import StartDate from "./components/StartDate";

type PlanProp = {
  id: string;
  onDelete: (planId: string) => Promise<void>;
  isCurrentPlan?: boolean | undefined;
};

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: "100%",
    backgroundColor: colors.white,
    padding: theme.spacing(2, 2),
    marginTop: theme.spacing(2),
  },
}));

const planFragment = graphql(`
  fragment Plan on Program {
    id
    clientId
    description
    endDate
    startDate
    name
    published
    mealPlan {
      id
      recommendedCalories
    }
  }
`);

const Plan: React.FC<PlanProp> = ({ id, onDelete, isCurrentPlan }) => {
  const { classes } = useStyles();
  const [plan] = useFragment(planFragment, `Program:${id}`);
  const duration = differenceInDays(
    new Date(plan?.endDate != undefined ? plan.endDate : new Date()),
    new Date(plan?.startDate != undefined ? plan.startDate : new Date())
  );

  const handleDeletePlan = () => onDelete(plan?.id ?? "");

  if (plan == null) return null;

  return (
    <div className={classes.root}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box display="flex" flexDirection="row">
          <Days duration={duration + 1} />
          <Name
            name={plan?.name}
            recommendedCalories={plan?.mealPlan?.recommendedCalories}
            isCurrentPlan={isCurrentPlan ?? false}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <PublishPlanSwitch
            isPublished={plan?.published ?? false}
            planId={plan?.id ?? ""}
          />
          <StartDate startDate={plan?.startDate} />
          <EndDate endDate={plan?.endDate} />
          <OptionMenu program={plan} onDelete={handleDeletePlan} />
        </Box>
      </Box>
    </div>
  );
};

export default Plan;
